import { useState } from "react";
import type { ReactNode } from "react";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import { PlusButton } from "@/components/design/PlusButton";
import { Collapse } from "@/components/design/Collapse";
import { Select } from "@/components/design/Select";
import { PersonLink } from "@/components/common/PersonLink";
import { ProjectEditBox } from "./ProjectEditBox";
import { ProjectEventFields } from "./ProjectEventFields";
import { ProjectPackageFields, ProjectTaskFields } from "./ProjectTaskFields";
import { dayMs } from "./project-schedule";
import { EVENT_KIND_LABELS, type PlanPerson, type ProjectEvent, type ProjectPackage, type ProjectTask } from "./project-plan";

// Projektfokus in der Optik der Ablauf-Seite: Marke am Strang, Titel, darunter
// eine Zeile Meta. Im Ruhezustand steht dort nur, was wahr ist - kein Raster
// aus Eingabefeldern, das über die halbe Seite läuft.
//
// Bearbeitet wird die ZEILE, nicht ein Feld darin: die ganze Fläche ist
// anklickbar und klappt den Kasten auf, und erst dort wird der Titel zum
// Eingabefeld. Vorher war der Titel immer schon ein Inline-Feld und nur die
// Datumszeile öffnete den Kasten - wer auf den Namen zielte, landete im
// Textfeld statt im Bearbeiten, und wer den Kasten wollte, musste das Datum
// treffen. Die Linie um die Zeile sagt jetzt, dass sie als Ganzes etwas tut.
//
// Pakete und Termine stehen in EINER Liste, nach Datum sortiert, und
// unterscheiden sich durch ihre Marke: Punkt für einen Zeitraum, Raute für einen
// Zeitpunkt. Zwei getrennte Listen würden genau das zerreissen, worum es hier
// geht - die Reihenfolge, in der die Dinge passieren.
//
// In WELCHE Richtung sie passieren, entscheidet der Mensch: neueste zuerst liest
// sich wie ein Lebenslauf und zeigt oben, was ansteht; älteste zuerst liest sich
// wie eine Chronik und zeigt, wie es dazu kam. Beides ist für einen Plan
// legitim, deshalb ist es eine Einstellung und keine Festlegung. Die Pfeilspitze
// am Strang sagt, welche gerade gilt - ohne sie müsste man Daten vergleichen,
// um die Leserichtung zu erraten.

/** Leserichtung der Liste. Steuert Sortierung und Richtung der Pfeilspitze. */
export type PlanOrder = "newestFirst" | "oldestFirst";

function formatDay(intl: IntlShape, iso: string) {
  const ms = dayMs(iso);
  if (!Number.isFinite(ms)) return iso;
  return intl.formatDate(ms, { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" });
}

function formatRange(intl: IntlShape, start: string, end: string) {
  return `${formatDay(intl, start)} – ${formatDay(intl, end)}`;
}

/**
 * Eine Zeile im Ruhezustand. Die ganze Fläche öffnet den Bearbeitungskasten,
 * der Titel darin ist zusätzlich ein echter Knopf.
 *
 * Warum beides: Die Fläche ist die Mausbequemlichkeit und trägt deshalb bewusst
 * KEINE Knopf-Rolle - in der Meta-Zeile steht ein Link aufs Profil, und ein Link
 * in einem Knopf ist ungültiges Markup. Der Titel-Knopf ist der Halt für die
 * Tastatur: er ist anfahrbar, trägt den Namen der Zeile als Beschriftung und
 * löst dasselbe aus.
 */
function PlanRow({
  title,
  meta,
  action,
  onOpen,
  editHint,
  widthClass,
  id,
}: {
  title: ReactNode;
  meta?: ReactNode;
  /** Schaltfläche neben dem Titel, etwa das Plus für eine neue Aufgabe. */
  action?: ReactNode;
  /** Fehlt, wenn man nur lesen darf: dann ist die Zeile Text, ohne Bearbeiten und ohne `action`. */
  onOpen?: () => void;
  editHint: string;
  /**
   * Dieselbe Breitengrenze wie der Bearbeitungskasten der Zeile. Ohne sie liefe
   * die getönte Fläche bis zum rechten Rand der Box, also über hunderte Pixel
   * Leere, und Ruhe- und Bearbeitungszustand hätten verschiedene Kanten.
   */
  widthClass: string;
  /** Testanker: am Öffnen-Knopf, `${id}-meta` an der Zeile darunter. */
  id: string;
}) {
  return (
    <div
      /* Optik der Kandidatenzeile im Match-Ergebnis (ResultPanel): kein Rahmen,
         sondern eine Tönung beim Überfahren. Ein Rahmen um jede Zeile zerlegt
         die Liste in Kästen und nimmt dem Strang die Führung; die Tönung sagt
         dasselbe, ohne etwas zu zeichnen. Eckig statt abgerundet, weil eine
         Zeile keine Kachel ist. Die negative Aussenkante lässt die Tönung
         etwas über den Text hinausgehen, damit sie nicht am Buchstaben klebt. */
      className={`group/row -mx-2 px-2 py-2 ${onOpen ? "cursor-pointer transition hover:bg-ink/[0.06]" : ""} ${widthClass}`}
      onClick={onOpen}
      title={onOpen ? editHint : undefined}
    >
      <div className="flex items-center gap-2">
        {onOpen ? (
          <button
            id={id}
            type="button"
            className="min-w-0 rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui"
            onClick={(e) => {
              // Ohne das feuert erst der Knopf und dann die Fläche darunter.
              e.stopPropagation();
              onOpen();
            }}
          >
            {title}
          </button>
        ) : (
          <span className="min-w-0">{title}</span>
        )}
        {onOpen ? action : null}
      </div>
      {meta ? (
        <div id={`${id}-meta`} className="smycles-field-meta mt-0.5 flex min-w-0 items-center gap-1.5 text-ink-muted">{meta}</div>
      ) : null}
    </div>
  );
}

/**
 * Die Spitze am Ende des Strangs. Reines CSS-Dreieck aus Rahmenkanten, damit
 * keine Icon-Bibliothek für einen Pfeil dazukommt.
 *
 * Sie sitzt dort, wo die Zeit HINLÄUFT, nicht wo die Liste endet: bei "neueste
 * zuerst" laufen die Daten nach oben, also zeigt sie nach oben und steht über
 * der ersten Zeile. Andersherum unten. Ohne das zeigte der Pfeil bei einer der
 * beiden Richtungen in die falsche.
 */
function TimelineArrow({ up }: { up: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`absolute -left-[4px] h-0 w-0 border-l-4 border-r-4 border-l-transparent border-r-transparent ${
        up ? "-top-[7px] border-b-[7px] border-b-ink/15" : "-bottom-[7px] border-t-[7px] border-t-ink/15"
      }`}
    />
  );
}

/**
 * Der Profil-Link in der Meta-Zeile. Er hält den Klick an, weil ein Klick auf
 * einen Namen aufs Profil will und nicht ins Bearbeiten der Zeile.
 */
function MetaPerson({ person }: { person: PlanPerson }) {
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <PersonLink person={person} size="sm" />
    </span>
  );
}

export function ProjectPlanList({
  packages,
  events,
  people,
  lookupPerson,
  onChange,
  onEventsChange,
  onAddTask,
  onAddPackage,
  onAddEvent,
  readOnly = false,
}: {
  packages: readonly ProjectPackage[];
  events: readonly ProjectEvent[];
  /** Zuweisbare Menschen für das Auswahlfeld einer Aufgabe. */
  people: readonly PlanPerson[];
  /** Name und Bild zu einer bereits zugewiesenen Id. */
  lookupPerson: (id: string | null) => PlanPerson | null;
  onChange: (next: readonly ProjectPackage[]) => void;
  onEventsChange: (next: readonly ProjectEvent[]) => void;
  /** Gibt die Id der neuen Zeile zurück, damit sie gleich aufgeklappt wird. */
  onAddTask: (packageId: string) => string | null;
  onAddPackage: () => string;
  onAddEvent: () => string;
  /** Nur lesen (`projectRights`): keine Bearbeitungskästen, kein Hinzufügen. */
  readOnly?: boolean;
}) {
  const intl = useIntl();
  const [editingId, setEditingId] = useState<string | null>(null);
  // Die Seite kann nur lesbar werden, während ein Kasten offen ist (Unternehmenskontext verlassen, eigene
  // Zeile entfernt); jeder Kasten schreibt das Projekt, also bleibt er dann zu.
  const openId = readOnly ? null : editingId;
  /** Momentaufnahme der Reihenfolge, solange bearbeitet wird. Siehe unten. */
  const [frozenOrder, setFrozenOrder] = useState<readonly string[] | null>(null);
  // Die Leserichtung gehört an die Liste, die sie ordnet, nicht in eine Reihe
  // mit dem Fokus-Umschalter: der entscheidet, WELCHE Ansicht man sieht, und ist
  // damit die wichtigere Wahl. Zwei gleich aussehende Pillen nebeneinander
  // behaupten gleiches Gewicht.
  const [order, setOrder] = useState<PlanOrder>("newestFirst");
  // Zugeklappte Pakete. Als Menge der ZUGEKLAPPTEN: ein neu angelegtes Paket ist
  // damit von selbst offen, und seine erste Aufgabe landet nicht hinter einer
  // Klappe, die niemand geöffnet hat.
  const [closedPackages, setClosedPackages] = useState<readonly string[]>([]);
  const unnamed = intl.formatMessage({ id: "project.plan.unnamed", defaultMessage: "Ohne Namen" });
  const editHint = intl.formatMessage({ id: "project.plan.editRow", defaultMessage: "Bearbeiten" });
  // Ohne Datum ans Ende, und zwar in BEIDEN Richtungen: ein leeres Feld ist
  // keine Aussage über den Zeitpunkt und darf die Liste weder anführen noch
  // beschliessen, je nachdem wie man gerade sortiert. Deshalb `null` und ein
  // eigener Zweig im Vergleich - mit einer Unendlich-Zahl käme beim Vergleich
  // zweier undatierter Zeilen NaN heraus, und ein Vergleich, der NaN liefert,
  // sortiert unvorhersehbar.
  const at = (iso: string): number | null => {
    const ms = dayMs(iso);
    return Number.isFinite(ms) ? ms : null;
  };
  const newestFirst = order === "newestFirst";
  const sorted = [
    ...packages.map((pkg) => ({ kind: "package" as const, pkg, at: at(pkg.start) })),
    ...events.map((event) => ({ kind: "event" as const, event, at: at(event.date) })),
  ].sort((a, b) => {
    if (a.at === null) return b.at === null ? 0 : 1;
    if (b.at === null) return -1;
    return newestFirst ? b.at - a.at : a.at - b.at;
  });
  const idOf = (row: (typeof sorted)[number]) => (row.kind === "event" ? row.event.id : row.pkg.id);

  // SOLANGE EIN KASTEN OFFEN IST, BLEIBT DIE REIHENFOLGE STEHEN. Ohne das
  // sortiert die Liste bei jedem Tastendruck im Datumsfeld neu, und die Zeile,
  // die man gerade bearbeitet, rutscht einem unter der Hand weg. Gespeichert
  // wird trotzdem sofort - nur die POSITION wartet aufs Schliessen.
  //
  // Die eingefrorene Reihenfolge gilt nur, solange es dieselben Zeilen sind:
  // kommt eine dazu oder fällt eine weg, ist die Momentaufnahme veraltet und die
  // neue Zeile soll sofort an ihrem Platz stehen, statt hinten angehängt zu werden.
  const rows = (() => {
    if (!frozenOrder || !openId) return sorted;
    const frozen = new Set(frozenOrder);
    if (frozen.size !== sorted.length || !sorted.every((row) => frozen.has(idOf(row)))) return sorted;
    const rank = new Map(frozenOrder.map((id, index) => [id, index]));
    return [...sorted].sort((a, b) => rank.get(idOf(a))! - rank.get(idOf(b))!);
  })();

  function openRow(id: string) {
    setFrozenOrder(sorted.map(idOf));
    setEditingId(id);
  }

  function closeRow() {
    setEditingId(null);
    setFrozenOrder(null);
  }

  function patchPackage(id: string, patch: Partial<ProjectPackage>) {
    onChange(packages.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }

  function patchTask(packageId: string, taskId: string, patch: Partial<ProjectTask>) {
    const pkg = packages.find((p) => p.id === packageId);
    if (!pkg) return;
    patchPackage(packageId, { tasks: pkg.tasks.map((t) => (t.id === taskId ? { ...t, ...patch } : t)) });
  }

  function patchEvent(id: string, patch: Partial<ProjectEvent>) {
    onEventsChange(events.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  }

  if (rows.length === 0) {
    return (
      <div>
        {/* Leerer Plan: sagen, was hierher gehört, und die eine Handlung anbieten. */}
        <p className="smycles-field-meta max-w-[52ch] text-ink-muted">
          <FormattedMessage
            id="project.plan.empty"
            defaultMessage="Noch keine Aufgabenpakete. Ein Aufgabenpaket ist ein Stück Arbeit mit Zeitraum, darin stehen die Aufgaben."
          />
        </p>
        {readOnly ? null : (
          <div className="mt-3 flex flex-wrap gap-4">
            <PlusButton
              id="project-plan-add-package"
              label={intl.formatMessage({ id: "project.plan.package", defaultMessage: "Aufgabenpaket" })}
              onClick={() => setEditingId(onAddPackage())}
            />
            <PlusButton
              id="project-plan-add-event"
              label={intl.formatMessage({ id: "project.plan.event", defaultMessage: "Termin" })}
              onClick={() => setEditingId(onAddEvent())}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Beschriftetes Auswahlfeld wie in der Share-Suche (ShareSortSelect):
          dieselbe Anordnung aus kleinem Label und Feld, damit "sortieren" im
          Produkt überall gleich aussieht. */}
      <label className="mb-3 inline-flex items-center gap-2">
        <span className="text-smycles-micro tracking-[0.2em] text-ink-muted">
          {intl.formatMessage({ id: "project.plan.order.label", defaultMessage: "Sortieren" })}
        </span>
        <Select
          id="project-plan-order"
          variant="pill"
          hideEmpty
          value={order}
          onChange={(next) => setOrder(next as PlanOrder)}
          options={[
            {
              value: "newestFirst",
              label: intl.formatMessage({ id: "project.plan.order.newest", defaultMessage: "Neueste zuerst" }),
            },
            {
              value: "oldestFirst",
              label: intl.formatMessage({ id: "project.plan.order.oldest", defaultMessage: "Älteste zuerst" }),
            },
          ]}
        />
      </label>

      <ol className="relative ml-[5px] border-l border-ink/15 pl-6">
        <TimelineArrow up={newestFirst} />
        {rows.map((row) => {
          if (row.kind === "event") {
            const event = row.event;
            return (
              <li key={event.id} className="relative pb-7 last:pb-0">
                {/* Raute statt Punkt: ein Zeitpunkt, kein Zeitraum. Dieselbe
                    Unterscheidung wie in der Swimlane, damit beide Ansichten
                    dasselbe Vokabular sprechen. Die Marke sitzt auf Höhe der
                    ersten Textzeile, also unterhalb der Polsterung der Fläche. */}
                {/* Gleicher Versatz wie der Paket-Punkt: die Drehung um 45 Grad
                    laesst die Raute breiter WIRKEN, verschiebt ihre Mitte aber
                    nicht. Mit einem eigenen Wert sass sie neben dem Strang. */}
                <span
                  aria-hidden="true"
                  className={`absolute -left-[29px] top-[13px] h-2.5 w-2.5 rotate-45 ${
                    event.kind === "meeting" ? "bg-page ring-1 ring-accent-decoration/60" : "bg-accent"
                  }`}
                />
                {openId === event.id ? (
                  <div className="max-w-md">
                    <ProjectEditBox
                      idPrefix={`project-plan-event-${event.id}`}
                      onDone={closeRow}
                      onDelete={() => {
                        onEventsChange(events.filter((e) => e.id !== event.id));
                        closeRow();
                      }}
                      deleteLabel={intl.formatMessage({
                        id: "project.plan.deleteEvent",
                        defaultMessage: "Termin löschen",
                      })}
                      confirmQuestion={intl.formatMessage({
                        id: "project.plan.deleteEventConfirm",
                        defaultMessage: "Termin löschen?",
                      })}
                    >
                      <ProjectEventFields
                        event={event}
                        onChange={(patch) => patchEvent(event.id, patch)}
                        idPrefix={`project-plan-event-${event.id}`}
                      />
                    </ProjectEditBox>
                  </div>
                ) : (
                  <PlanRow
                    id={`project-plan-event-${event.id}`}
                    onOpen={readOnly ? undefined : () => openRow(event.id)}
                    editHint={editHint}
                    widthClass="max-w-[calc(28rem+1rem)]"
                    title={<span className="smycles-field-value font-medium">{event.title || unnamed}</span>}
                    meta={
                      <>
                        {formatDay(intl, event.date)}
                        {" · "}
                        {intl.formatMessage(EVENT_KIND_LABELS[event.kind])}
                      </>
                    }
                  />
                )}
              </li>
            );
          }
          const pkg = row.pkg;
          const owner = lookupPerson(pkg.assigneeId);
          return (
            <li key={pkg.id} className="relative pb-7 last:pb-0">
              {/* Ink, nicht Signalfarbe: die trägt allein der Termin. Ein Paket ist
                  Arbeit, kein Zeitpunkt, auf den es ankommt. */}
              <span aria-hidden="true" className="absolute -left-[29px] top-[13px] h-2.5 w-2.5 rounded-full bg-ink" />

              {openId === pkg.id ? (
                <div className="max-w-md">
                  <ProjectEditBox
                    idPrefix={`project-plan-package-${pkg.id}`}
                    onDone={closeRow}
                    onDelete={() => {
                      onChange(packages.filter((p) => p.id !== pkg.id));
                      closeRow();
                    }}
                    deleteLabel={intl.formatMessage({
                      id: "project.plan.deletePackage",
                      defaultMessage: "Aufgabenpaket löschen",
                    })}
                    confirmQuestion={intl.formatMessage(
                      {
                        id: "project.plan.deletePackageConfirm",
                        defaultMessage:
                          "{count, plural, =0 {Aufgabenpaket löschen?} one {Mit einer Aufgabe löschen?} other {Mit # Aufgaben löschen?}}",
                      },
                      { count: pkg.tasks.length },
                    )}
                  >
                    <ProjectPackageFields
                      idPrefix={`project-plan-package-${pkg.id}`}
                      pkg={pkg}
                      people={people}
                      assignee={owner}
                      onChange={(patch) => patchPackage(pkg.id, patch)}
                      onAddTask={() => setEditingId(onAddTask(pkg.id))}
                    />
                  </ProjectEditBox>
                </div>
              ) : (
                <PlanRow
                  id={`project-plan-package-${pkg.id}`}
                  onOpen={readOnly ? undefined : () => openRow(pkg.id)}
                  editHint={editHint}
                  widthClass="max-w-[calc(28rem+1rem)]"
                  title={
                    <span className="flex min-w-0 items-center gap-1.5">
                      {/* Der Griff steht VOR dem Namen, wie in jeder Baumansicht:
                          er gehört zur Zeile, nicht zu ihren Werkzeugen rechts.
                          stopPropagation, weil ein Klick auf die Zeile sie zum
                          Bearbeiten öffnet - Zuklappen ist etwas anderes. */}
                      {pkg.tasks.length > 0 ? (
                        <button
                          type="button"
                          aria-expanded={!closedPackages.includes(pkg.id)}
                          aria-label={intl.formatMessage(
                            closedPackages.includes(pkg.id)
                              ? { id: "project.plan.expandPackage", defaultMessage: "Aufgaben zeigen" }
                              : { id: "project.plan.collapsePackage", defaultMessage: "Aufgaben verbergen" },
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            setClosedPackages((cur) =>
                              cur.includes(pkg.id) ? cur.filter((id) => id !== pkg.id) : [...cur, pkg.id],
                            );
                          }}
                          className="-ml-1 flex h-5 w-5 shrink-0 items-center justify-center rounded text-smycles-body leading-none text-ink-muted transition hover:text-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink-ui"
                        >
                          {closedPackages.includes(pkg.id) ? "▸" : "▾"}
                        </button>
                      ) : null}
                      <span className="smycles-field-value truncate font-medium">{pkg.name || unnamed}</span>
                      {closedPackages.includes(pkg.id) ? (
                        <span className="smycles-field-meta shrink-0 text-ink-muted">
                          <FormattedMessage
                            id="project.plan.taskCount"
                            defaultMessage="{count, plural, one {1 Aufgabe} other {# Aufgaben}}"
                            values={{ count: pkg.tasks.length }}
                          />
                        </span>
                      ) : null}
                    </span>
                  }
                  action={
                    /* Das Plus steht neben dem Titel, nicht am Zeilenende: rechts
                       aussen verliert es bei breitem Fenster jeden Bezug zu dem
                       Paket, zu dem es gehört, und man findet es dort nicht.
                       Sichtbar wird es beim Überfahren, wie in der Fachkompetenz-
                       Leiter; focus-visible und group-focus-within, weil es sonst
                       für die Tastatur unsichtbar bliebe. */
                    <button
                      type="button"
                      aria-label={intl.formatMessage({
                        id: "project.plan.addTaskHere",
                        defaultMessage: "Aufgabe zu diesem Aufgabenpaket hinzufügen",
                      })}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingId(onAddTask(pkg.id));
                      }}
                      className="shrink-0 rounded-full p-1 text-ink-muted opacity-0 transition hover:text-ink-accent focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ink-ui group-hover/row:opacity-100 group-focus-within/row:opacity-100"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        aria-hidden="true"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </button>
                  }
                  meta={
                    /* Anders als bei einer Aufgabe steht hier NICHTS, solange
                       niemand zugewiesen ist - ein Paket ohne Zuständigen ist der
                       Normalfall, und "Ohne Zuweisung" an jeder Zeile wäre Rauschen
                       ohne Aussage. */
                    <>
                      {formatRange(intl, pkg.start, pkg.end)}
                      {owner ? (
                        <>
                          {" · "}
                          <MetaPerson person={owner} />
                        </>
                      ) : null}
                    </>
                  }
                />
              )}

              <Collapse open={!closedPackages.includes(pkg.id)}>
              <ul className="mt-3 space-y-2.5 border-l border-ink/10 pl-5">
                {pkg.tasks.map((task) => {
                  const person = lookupPerson(task.assigneeId);
                  return (
                    <li key={task.id} className="relative">
                      <span
                        aria-hidden="true"
                        className="absolute -left-[23px] top-[14px] h-2 w-2 rounded-full bg-page ring-1 ring-ink-ui"
                      />
                      {openId === task.id ? (
                        <div className="max-w-2xl">
                          <ProjectEditBox
                            idPrefix={`project-plan-task-${task.id}`}
                            onDone={closeRow}
                            onDelete={() => {
                              patchPackage(pkg.id, { tasks: pkg.tasks.filter((t) => t.id !== task.id) });
                              closeRow();
                            }}
                            deleteLabel={intl.formatMessage({
                              id: "project.plan.removeTask",
                              defaultMessage: "Aufgabe löschen",
                            })}
                            confirmQuestion={intl.formatMessage({
                              id: "project.plan.removeTaskConfirm",
                              defaultMessage: "Aufgabe löschen?",
                            })}
                          >
                            <ProjectTaskFields
                              idPrefix={`project-plan-task-${task.id}`}
                              task={task}
                              people={people}
                              assignee={person}
                              onChange={(patch) => patchTask(pkg.id, task.id, patch)}
                            />
                          </ProjectEditBox>
                        </div>
                      ) : (
                        <PlanRow
                          id={`project-plan-task-${task.id}`}
                          onOpen={readOnly ? undefined : () => openRow(task.id)}
                          editHint={editHint}
                          widthClass="max-w-[calc(42rem+1rem)]"
                          title={<span className="smycles-field-value">{task.title || unnamed}</span>}
                          meta={
                            <>
                              {formatRange(intl, task.start, task.end)}
                              {" · "}
                              {person ? (
                                <MetaPerson person={person} />
                              ) : (
                                intl.formatMessage({
                                  id: "project.lanes.unassigned",
                                  defaultMessage: "Ohne Zuweisung",
                                })
                              )}
                            </>
                          }
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
              </Collapse>
            </li>
          );
        })}
      </ol>

      {readOnly ? null : (
        <div className="mt-4 flex flex-wrap gap-4 pl-6">
          <PlusButton
            id="project-plan-add-package"
            label={intl.formatMessage({ id: "project.plan.package", defaultMessage: "Aufgabenpaket" })}
            onClick={() => setEditingId(onAddPackage())}
          />
          <PlusButton
            id="project-plan-add-event"
            label={intl.formatMessage({ id: "project.plan.event", defaultMessage: "Termin" })}
            onClick={() => setEditingId(onAddEvent())}
          />
        </div>
      )}
    </div>
  );
}
